import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import { FiberNew } from '@mui/icons-material';
import {
    DateInput,
    FileField,
    FileInput,
    Form,
    required,
    TextInput,
    useCreate,
    useNotify,
    useTranslate,
} from 'react-admin';
import { useNavigate, useParams } from 'react-router';
import {
    AVATAR_RESOURCE_SUPPORTED,
    AVATARS,
    CONTRACT_METADATA_AVATAR_TYPE_ALPHA_ID,
    FILE_MEDIA_TYPE,
    FINGER_ID,
    FINGERS,
    GROUP_AVATAR_SITE_PREFIX,
    INCIDENT_SERVICE_CONTRACT,
    MEDIAS,
    VALIDATED_UNTIL_DATE_AVATAR_PROPERTY,
} from '@/utils/CONST';
import Box from '@mui/material/Box';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { composeUri } from '@/utils/compose';
import useGetAlphaId from '@/utils/useGetAlphaId';
import useGetMetadataAvatarTypes from '@/hooks/useGetMetadataAvatarTypes';
import { bytesToMegabytes, getFileBase64 } from '@/utils/files';
import extractIdFromURI from '@/utils/id';
import useGetGroupAvatars from '@/hooks/useGetGroupAvatars';

const validUntilDateFormKey = 'validUntilDate';

const CreateContractDialog = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const handleClose = () => {
        navigate(-1);
    };
    const notify = useNotify();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const { placeId, siteId } = useParams();
    const { isLoadingGetAlphaId, getAlphaId } = useGetAlphaId({
        service: INCIDENT_SERVICE_CONTRACT,
    });
    const { firstMetadataAvatarType: metadataAvatarType } = useGetMetadataAvatarTypes({
        filters: {
            alphaId: CONTRACT_METADATA_AVATAR_TYPE_ALPHA_ID,
        },
    });

    const { groupAvatars, firstGroupAvatarId, isLoading: isLoadingGroup } = useGetGroupAvatars({
        filters: {
            alphaId: `${GROUP_AVATAR_SITE_PREFIX}${siteId}`,
        },
    });

    const handleSubmit = async ({ [validUntilDateFormKey]: validUntilDate, name, media, description }: any) => {
        getAlphaId().then(({ counter }: { counter: number; identifier: string }) => {
            create(
                AVATARS,
                {
                    data: {
                        parent: composeUri(AVATARS, placeId),
                        name,
                        description,
                        alphaId: `C:${counter}`,
                        mcs: [],
                        generateMC: {
                            finger: composeUri(FINGERS, FINGER_ID),
                            quantity: 1,
                        },
                        rawProperties: [
                            {
                                name: VALIDATED_UNTIL_DATE_AVATAR_PROPERTY,
                                value: validUntilDate,
                                fieldType: 'string',
                                private: false,
                            },
                        ],
                        groupAvatars: groupAvatars && groupAvatars.length > 0 && firstGroupAvatarId ? [firstGroupAvatarId] : [],
                        metadataAvatarType: metadataAvatarType?.id,
                    },
                    meta: {
                        realResource: `v1/${AVATARS}`,
                    },
                },
                {
                    onSuccess: async (avatar: any) => {
                        const fileData: any = await getFileBase64(media.rawFile);

                        const finalMedia = {
                            mediaType: FILE_MEDIA_TYPE,
                            resourceSupported: AVATAR_RESOURCE_SUPPORTED,
                            fileName: media.title,
                            base64data: fileData,
                            resourceId: extractIdFromURI(avatar?.id),
                        };

                        create(
                            MEDIAS,
                            {
                                data: finalMedia,
                            },
                            {
                                onSuccess: () => {
                                    notify('ra.notification.created', { type: 'success' });
                                    handleClose();
                                },
                            },
                        );
                    },
                },
            );
        });
    };

    return (
        <DialogTemplate
            icon={<FiberNew />}
            title={translate('contract.create.label')}
            open={true}
            onClose={handleClose}
            maxWidth={'sm'}
        >
            <Form onSubmit={handleSubmit} shouldUnregister>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            pt: 1,
                        }}
                    >
                        <TextInput
                            source='name'
                            label={'contract.create.name'}
                            helperText={false}
                            validate={required()}
                        />

                        <TextInput
                            source='description'
                            label={'contract.create.description'}
                            helperText={false}
                            validate={required()}
                            multiline
                        />

                        <DateInput
                            source={validUntilDateFormKey}
                            label={'contract.create.expireDate'}
                            helperText={false}
                            validate={required()}
                        />

                        <FileInput
                            source='media'
                            label={false}
                            maxSize={30000000}
                            sx={{
                                '& .previews': {
                                    display: 'flex',
                                    justifyContent: 'center',
                                },
                            }}
                            options={{
                                onDropRejected: (files: any) => {
                                    if (files.length > 0) {
                                        if (files[0].errors[0].code === 'file-too-large') {
                                            notify('messages.file_too_large', {
                                                type: 'warning',
                                                messageArgs: {
                                                    size: bytesToMegabytes(files[0].file.size),
                                                },
                                            });
                                        }
                                    }
                                },
                            }}
                            validate={required()}
                        >
                            <FileField source='src' title='title' />
                        </FileInput>
                    </Box>

                    <ButtonsBlock>
                        <Button onClick={handleClose} disabled={isLoadingGetAlphaId || isLoadingCreate}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            loading={isLoadingGetAlphaId || isLoadingCreate || isLoadingGroup || !groupAvatars}
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreateContractDialog;
